import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IonicModule, ModalController, NavController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { ComposePage } from 'src/app/components/compose/compose.page';
import { PROJECT_MEMBER_ROLES, routes } from 'src/app/constants';
import { createCommunityRecipient, downloadCSV, isDev, Recipient } from 'src/app/utils/utils';
import { FetchDataRequest, FetchDataResponse, createDataSource } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { CommunityMember, WhereClause, Organizer } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service.js';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service.js';
import { UserGroupsService } from 'src/services/user-groups.service';
import { UsersService } from 'src/services/users.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule, NgxTippyModule, SharedDirectivesModule],
  selector: 'app-channel-admin-members',
  templateUrl: './channel-admin-members.page.html',
  styleUrls: ['./channel-admin-members.page.scss'],
})
export class ChannelAdminMembersPage implements OnInit {
  channel: Partial<Organizer>;
  channelId: string;
  isSuperAdmin: boolean = false;
  isDev: boolean = false;
  isProjectOwner: boolean = false;

  searchText: string;
  members: any[] = [];
  selectedMembers: any[] = [];
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  memberTypeFilter: 'all' | 'agents' | 'customers' = 'all';
  limit = 25;
  sortField = 'user.name';
  sortDesc = false;

  internalEntityOverrides: {
    allowlist?: string[] | null;
    blocklist?: string[] | null;
  } = { allowlist: null, blocklist: null };
  overrideAllowlist: boolean = false;
  overrideBlocklist: boolean = false;
  isOverridesExpanded: boolean = false;
  userGroups: any[] = [];
  users: any[] = [];
  entityLookup: { [key: string]: string } = {};
  isSavingSettings = false;

  fetchMembers = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<CommunityMember>>> => {
    const where = [
      this.memberTypeFilter === 'agents'
        ? {
            field: 'isExternal',
            operator: '==',
            value: false,
          }
        : this.memberTypeFilter === 'customers'
          ? {
              field: 'isExternal',
              operator: '==',
              value: true,
            }
          : undefined,
      this.searchText
        ? {
            field: 'user.name',
            operator: 'like',
            value: `%${this.searchText}%`,
          }
        : undefined,
    ].filter((where) => !!where) as WhereClause[];

    const results: any = await this.apiService.postPromise(`/customers/${this.channelId}/members/list`, {
      limit,
      order,
      descending,
      cursor,
      where,
    });

    return results;
  };

  membersDataSource = createDataSource(this.fetchMembers);

  constructor(
    router: Router,
    private modalCtrl: ModalController,
    private navController: NavController,
    private msgSrvc: MessageService,
    public authService: AuthService,
    private customersService: CustomersService,
    private apiService: ApiService,
    private slackService: SlackService,
    private userGroupsService: UserGroupsService,
    private usersService: UsersService,
    private searchablePopoverService: SearchablePopoverService,
  ) {
    this.channelId = router.url.split('/')[3];
  }

  async ngOnInit() {
    this.isLoading = true;

    await this.getChannel();
    await this.loadUserGroups();
    await this.loadUsers();

    if (this.channel.slackChannelId) {
      await this.membersDataSource.firstPage();
    }
    this.isSuperAdmin = this.authService.isSuperAdmin;
    this.isDev = isDev();

    this.isProjectOwner = this.authService.isProjectOwner(this.channel.projectId);

    this.isLoading = false;
  }

  syncChannel() {
    if (this.channelId) {
      this.slackService.syncChannel(this.channelId);
      this.msgSrvc.show('Submitted a background task to sync the channel!');
    } else {
      this.msgSrvc.showError('missing slackChannelId!');
    }
  }

  async reloadMembers() {
    this.isLoading = true;

    await this.membersDataSource.firstPage();

    this.isLoading = false;
  }

  isMemberAnAgent(member: any) {
    return [PROJECT_MEMBER_ROLES.OWNER, PROJECT_MEMBER_ROLES.AGENT].includes(member.user?.projectUser?.role);
  }

  downloadMembers() {
    const headers = ['name', 'email', 'phoneNumber', 'isExternal', 'createdAt'];

    downloadCSV(
      this.membersDataSource.lastResponse.data.map((member) => ({
        name: member.user?.name,
        email: member.user?.email,
        phoneNumber: member.user?.phoneNumber,
        isExternal: member.isExternal ?? member.user?.isExternal,
        createdAt: member.user?.createdAt,
      })),
      headers,
      `member-list-${this.channelId}`,
    );
  }

  async sendMessage(emails?: (string | Recipient)[]) {
    if (!emails) {
      if (this.selectedMembers.length) {
        emails = this.selectedMembers.map((member) => member.user?.email).filter((e) => !!e);
      } else {
        emails = [createCommunityRecipient(this.channel as Organizer)];
      }
    }

    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        communityId: this.channelId,
        emails,
        showTemplates: true,
      },
      cssClass: 'modal-large',
    });
    modal.present();
  }

  viewProfile(user: any) {
    this.navController.navigateForward(`/${routes.PEOPLE}${user.user?.isExternal ? '/external' : ''}/${user.userId}`);
  }

  onSelect({ selected }) {
    this.selectedMembers.splice(0, this.selectedMembers.length);
    this.selectedMembers.push(...selected);
  }

  async getChannel() {
    const channel = await this.customersService.get(this.channelId);
    this.channel = channel;

    const sourceOverrides = channel.internalEntityOverrides || {};

    this.overrideAllowlist = sourceOverrides.allowlist !== null && sourceOverrides.allowlist !== undefined;
    this.overrideBlocklist = sourceOverrides.blocklist !== null && sourceOverrides.blocklist !== undefined;

    this.internalEntityOverrides = {
      allowlist: this.overrideAllowlist ? [...(sourceOverrides.allowlist || [])] : null,
      blocklist: this.overrideBlocklist ? [...(sourceOverrides.blocklist || [])] : null,
    };
  }

  manageAgents() {
    if (this.isProjectOwner) {
      this.navController.navigateRoot(
        `${routes.DASHBOARD}/${routes.SETTINGS}/${routes.PROJECTS}/${this.channel.projectId}/members`,
      );
    } else {
      this.msgSrvc.showError('You are not authorized to manage agents for this project');
    }
  }

  async loadUserGroups() {
    try {
      const response = await this.userGroupsService.list();
      this.userGroups = response.data || [];
      this.userGroups.forEach((group) => {
        this.entityLookup[group.id] = `${group.name} (Group)`;
      });
    } catch (error) {
      console.error('Error loading user groups:', error);
    }
  }

  async loadUsers() {
    try {
      const response = await this.usersService.list();
      this.users = response.data || [];
      this.users.forEach((user) => {
        this.entityLookup[user.id] = `${user.name || user.email} (User)`;
      });
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }

  getEntityName(id: string): string {
    return this.entityLookup[id] || id;
  }

  onAllowlistOverrideChange() {
    if (!this.internalEntityOverrides) {
      this.internalEntityOverrides = { allowlist: null, blocklist: null };
    }
    if (this.overrideAllowlist) {
      if (this.internalEntityOverrides.allowlist === null || this.internalEntityOverrides.allowlist === undefined) {
        this.internalEntityOverrides.allowlist = [];
      }
    } else {
      this.internalEntityOverrides.allowlist = null;
    }
  }

  removeFromAllowlist(index: number) {
    if (this.internalEntityOverrides.allowlist && this.internalEntityOverrides.allowlist.length > index) {
      this.internalEntityOverrides.allowlist.splice(index, 1);
    }
  }

  onBlocklistOverrideChange() {
    if (!this.internalEntityOverrides) {
      this.internalEntityOverrides = { allowlist: null, blocklist: null };
    }
    if (this.overrideBlocklist) {
      if (this.internalEntityOverrides.blocklist === null || this.internalEntityOverrides.blocklist === undefined) {
        this.internalEntityOverrides.blocklist = [];
      }
    } else {
      this.internalEntityOverrides.blocklist = null;
    }
  }

  removeFromBlocklist(index: number) {
    if (this.internalEntityOverrides.blocklist && this.internalEntityOverrides.blocklist.length > index) {
      this.internalEntityOverrides.blocklist.splice(index, 1);
    }
  }

  async addToAllowlist(event: any) {
    if (!this.internalEntityOverrides.allowlist) {
      this.internalEntityOverrides.allowlist = [];
    }
    this.searchablePopoverService.assignees({
      event,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: false,
      includeGroups: true,
      callback: async (id: string, _name: string, _object: any) => {
        if (!this.internalEntityOverrides) {
          this.internalEntityOverrides = { allowlist: [], blocklist: [] };
        }
        if (!this.internalEntityOverrides.allowlist) {
          this.internalEntityOverrides.allowlist = [];
        }
        if (!this.internalEntityOverrides.allowlist.includes(id)) {
          this.internalEntityOverrides.allowlist.push(id);
        }
      },
    });
  }

  async addToBlocklist(event: any) {
    if (!this.internalEntityOverrides.blocklist) {
      this.internalEntityOverrides.blocklist = [];
    }
    this.searchablePopoverService.assignees({
      event,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: false,
      includeGroups: true,
      callback: async (id: string, _name: string, _object: any) => {
        if (!this.internalEntityOverrides) {
          this.internalEntityOverrides = { allowlist: [], blocklist: [] };
        }
        if (!this.internalEntityOverrides.blocklist) {
          this.internalEntityOverrides.blocklist = [];
        }
        if (!this.internalEntityOverrides.blocklist.includes(id)) {
          this.internalEntityOverrides.blocklist.push(id);
        }
      },
    });
  }

  async saveChannelSettings() {
    if (!this.channel || !this.channelId) {
      this.msgSrvc.showError('Channel data not loaded.');
      return;
    }

    const updates: Partial<Organizer> = {
      internalEntityOverrides: {
        allowlist: this.overrideAllowlist ? this.internalEntityOverrides.allowlist ?? [] : null,
        blocklist: this.overrideBlocklist ? this.internalEntityOverrides.blocklist ?? [] : null,
      },
    };

    this.isSavingSettings = true;
    try {
      await this.customersService.update(this.channelId, updates);
      if (this.channel) {
        this.channel.internalEntityOverrides = updates.internalEntityOverrides;
      }
      this.msgSrvc.show('✔︎ Member classification settings updated');
    } catch (err) {
      console.error('Error updating channel member settings:', err);
      this.msgSrvc.showError('Failed to update member classification settings');
    } finally {
      this.isSavingSettings = false;
    }
  }
}
